import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Briefcase, MapPin, Clock, ChevronRight, Plus, Search, Star, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceTypes = [
  { key: 'all', label: 'All' },
  { key: 'auto', label: 'Auto & Mechanics' },
  { key: 'beauty', label: 'Hair & Beauty' },
  { key: 'wellness', label: 'Wellness' },
  { key: 'trade', label: 'Home & Trade' },
  { key: 'other', label: 'Other' },
];

const ServicesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(`${API_URL}/api/services`);
        if (res.ok) {
          const data = await res.json();
          setServices(data);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const filteredServices = services.filter(s => {
    if (activeFilter !== 'all' && s.category !== activeFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return s.title.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <Layout title="Services" showBack>
      {/* Hero */}
      <div className="bg-minted-900 text-white px-6 py-6">
        <p className="text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-1">New on Minted</p>
        <h2 className="font-display text-xl font-medium tracking-tight">Local Services</h2>
        <p className="text-stone-400 text-sm mt-1">Find trusted local professionals near you</p>
        
        <div className="mt-4 flex gap-2 items-center">
          <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Free for 1 week</span>
          <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Then $1/week</span>
          <button
            onClick={() => navigate('/services/create')}
            className="ml-auto text-xs bg-white text-minted-900 px-4 py-2 rounded-sm font-medium tracking-wider uppercase hover:bg-stone-100 transition-colors"
            data-testid="list-service-cta"
          >
            + List a Service
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-4">
        <div className="flex items-center bg-white border border-stone-200 rounded-sm overflow-hidden">
          <Search className="w-4 h-4 text-stone-400 ml-3" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm py-2.5 px-3 outline-none text-stone-900 placeholder-stone-400"
            data-testid="services-search"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 px-4 pt-3 overflow-x-auto pb-2 scrollbar-hide">
        {serviceTypes.map((type) => (
          <button
            key={type.key}
            onClick={() => setActiveFilter(type.key)}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-medium whitespace-nowrap transition-colors tracking-wider uppercase ${
              activeFilter === type.key
                ? 'bg-minted-900 text-white'
                : 'bg-white text-stone-500 border border-stone-200'
            }`}
            data-testid={`filter-${type.key}`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Services List */}
      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-stone-100 rounded-sm p-4 animate-pulse">
                <div className="h-4 bg-stone-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-stone-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-stone-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-stone-500 font-medium">No services listed yet</p>
            <p className="text-stone-400 text-sm mt-1">Be the first to list your service</p>
            {isAuthenticated && (
              <Button
                className="mt-4 bg-minted-900 hover:bg-minted-700 rounded-sm"
                onClick={() => navigate('/services/create')}
                data-testid="create-service-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                List Your Service
              </Button>
            )}
          </div>
        ) : (
          filteredServices.map((service) => (
            <div
              key={service.id}
              className="bg-white border border-stone-100 rounded-sm p-4 cursor-pointer hover:border-stone-300 transition-colors"
              onClick={() => navigate(`/services/${service.id}`)}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-medium text-stone-900 text-sm">{service.title}</h3>
                  <p className="text-stone-400 text-xs mt-0.5">{service.provider_name}</p>
                </div>
                <Badge className="bg-minted-50 text-minted-700 text-[10px] rounded-sm border-0 tracking-wider uppercase">
                  {service.category}
                </Badge>
              </div>
              {service.description && (
                <p className="text-stone-500 text-xs line-clamp-2 mb-2">{service.description}</p>
              )}
              <div className="flex items-center gap-3 text-[11px] text-stone-400">
                {service.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" strokeWidth={1.5} />
                    {service.location}
                  </span>
                )}
                {service.rating && (
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3" strokeWidth={1.5} />
                    {service.rating}
                  </span>
                )}
                <span className="flex items-center gap-1" data-testid={`service-views-${service.id}`}>
                  <Eye className="w-3 h-3" strokeWidth={1.5} />
                  {service.views || 0}
                </span>
                {service.free_until && new Date(service.free_until) > new Date() && service.subscription_status !== 'active' && (
                  <span className="flex items-center gap-1 text-emerald-500">
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    Free until {new Date(service.free_until).toLocaleDateString()}
                  </span>
                )}
                {user?.id === service.provider_id && service.free_until && new Date(service.free_until) <= new Date() && service.subscription_status !== 'active' && (
                  <span className="text-red-500 font-medium">Payment due</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* CTA to list service */}
      {isAuthenticated && filteredServices.length > 0 && (
        <div className="px-4 pb-6">
          <Button
            className="w-full bg-minted-900 hover:bg-minted-700 rounded-sm h-11"
            onClick={() => navigate('/services/create')}
            data-testid="list-service-cta"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            List Your Service
          </Button>
        </div>
      )}

      {!isAuthenticated && (
        <div className="px-4 pb-6">
          <div className="bg-white border border-stone-200 rounded-sm p-5 text-center">
            <Briefcase className="w-8 h-8 text-stone-300 mx-auto mb-2" strokeWidth={1.5} />
            <h3 className="font-display font-medium text-stone-900 mb-1">Offer a service?</h3>
            <p className="text-stone-500 text-xs mb-3">List your service free for the first week</p>
            <Button onClick={() => navigate('/register')} className="bg-minted-900 hover:bg-minted-700 rounded-sm" size="sm">
              Sign Up to List
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ServicesPage;
